import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import {
  CreateDeclarantSchema,
  CreateGoodLocationSchema,
  DeclarantListItemSchema,
  GetDeclarantUsersResultSchema,
  GetDeclarantUsersSchema,
  RetrieveDeclarantsResponseSchema,
  RetrieveDeclarantsSchema,
  RetrieveGoodLocationsResponseSchema,
  RetrieveGoodLocationsSchema,
  UpdateDeclarantSchema,
  UpdateGoodLocationSchema,
} from './schemas';

const c = initContract();

const responseMessage200 = z.object({
  data: z.object({ message: z.string() }),
});

export const declarantContract = c.router({
  getDeclarants: {
    method: 'GET',
    path: '/declarant',
    responses: {
      200: z.object({
        data: z.array(DeclarantListItemSchema),
      }),
    },
  },
  getDeclarantUsers: {
    method: 'GET',
    path: '/declarant/:declarantId/users',
    pathParams: GetDeclarantUsersSchema,
    responses: { 200: GetDeclarantUsersResultSchema },
  },
  getDeclarantById: {
    method: 'GET',
    path: '/declarant/:id',
    pathParams: z.object({ id: z.string() }),
    responses: {
      200: z.object({
        data: DeclarantListItemSchema,
      }),
    },
  },
  retrieveDeclarants: {
    method: 'POST',
    path: '/declarant/',
    body: RetrieveDeclarantsSchema,
    responses: {
      200: RetrieveDeclarantsResponseSchema,
      500: z.object({ message: z.string() }),
    },
  },
  addDeclarant: {
    method: 'POST',
    path: '/declarant/add',
    body: z.object({ declarantData: CreateDeclarantSchema }),
    responses: { 200: responseMessage200 },
  },
  updateDeclarant: {
    method: 'PATCH',
    path: '/declarant',
    body: z.object({ declarant: UpdateDeclarantSchema }),
    responses: { 200: responseMessage200 },
  },
  deleteDeclarant: {
    method: 'DELETE',
    path: '/declarant/:id',
    pathParams: z.object({ id: z.string() }),
    responses: { 200: responseMessage200 },
  },
  enableDeclarant: {
    method: 'PATCH',
    path: '/declarant/:id/enable',
    body: c.noBody(),
    pathParams: z.object({ id: z.string() }),
    responses: { 200: responseMessage200 },
  },
  addUserToDeclarant: {
    method: 'PATCH',
    path: '/declarant/:id/add-user/:email',
    body: c.noBody(),
    pathParams: z.object({ id: z.string(), email: z.string() }),
    responses: { 200: responseMessage200 },
  },
  sendGuaranteeQuery: {
    method: 'POST',
    path: '/declarant/send-guarantee-query',
    body: c.noBody(),
    responses: { 200: responseMessage200 },
  },
  generateCustomsQueryTestFile: {
    method: 'POST',
    path: '/declarant/generate-customs-query-test-file',
    body: c.noBody(),
    responses: {
      200: z.object({
        data: z.object({
          base64: z.string(),
          fileName: z.string(),
        }),
      }),
    },
  },
  createWallet: {
    method: 'POST',
    path: '/declarant/create-wallet',
    body: c.noBody(),
    responses: { 200: responseMessage200 },
  },
  checkCompanyEori: {
    method: 'PATCH',
    path: '/declarant/check-company-eori',
    body: z.object({ eori: z.string() }),
    responses: { 200: responseMessage200 },
  },
  checkCompanyVat: {
    method: 'PATCH',
    path: '/declarant/check-company-vat',
    body: z.object({ vat: z.string() }),
    responses: { 200: responseMessage200 },
  },
  checkCompanyBusinessLicense: {
    method: 'PATCH',
    path: '/declarant/check-company-business-license',
    body: z.object({ registrationNumber: z.string() }),
    responses: { 200: responseMessage200 },
  },
  updateLogo: {
    method: 'POST',
    path: '/declarant/update-logo',
    body: z.object({
      declarantId: z.string(),
      file: z.any(),
    }),
    responses: { 200: responseMessage200 },
    contentType: 'multipart/form-data',
  },
  deleteLogo: {
    method: 'DELETE',
    path: '/declarant/:declarantId/logo',
    body: c.noBody(),
    pathParams: z.object({ declarantId: z.string() }),
    responses: { 200: responseMessage200 },
  },
  getLogo: {
    method: 'GET',
    path: '/declarant/:declarantId/logo',
    pathParams: z.object({ declarantId: z.string() }),
    responses: {
      200: z.object({
        data: z.object({
          base64: z.string(),
          fileName: z.string(),
        }),
      }),
    },
  },
  createGoodLocation: {
    method: 'POST',
    path: '/declarant/:declarantId/good-location',
    pathParams: z.object({ declarantId: z.string() }),
    body: z.object({
      goodLocation: CreateGoodLocationSchema,
    }),
    responses: { 200: responseMessage200 },
  },
  updateGoodLocation: {
    method: 'PATCH',
    path: '/declarant/:declarantId/good-location',
    pathParams: z.object({ declarantId: z.string() }),
    body: z.object({
      goodLocation: UpdateGoodLocationSchema,
    }),
    responses: { 200: responseMessage200 },
  },
  deleteGoodLocation: {
    method: 'DELETE',
    path: '/declarant/:declarantId/good-location/:locationId',
    pathParams: z.object({ declarantId: z.string(), locationId: z.string() }),
    body: c.noBody(),
    responses: { 200: responseMessage200 },
  },
  retrieveGoodLocations: {
    method: 'POST',
    path: '/declarant/:declarantId/goods-locations',
    pathParams: z.object({ declarantId: z.string() }),
    body: RetrieveGoodLocationsSchema,
    responses: {
      200: RetrieveGoodLocationsResponseSchema,
      500: z.object({ message: z.string() }),
    },
  },
});
